import { useEffect, useRef, type Dispatch } from 'react'
import {
  canNavigateToStep,
  resolveNextStepFromSession,
  type CheckoutFlowAction,
  type CheckoutFlowState,
} from './CheckoutFlow.types'
import {
  buildCheckoutNextPathForLoginRedirect,
} from './useCheckoutFlowEffects/navigation'
import { bootstrapCheckoutFlow } from './useCheckoutFlowEffects/bootstrap'
import { refreshCheckoutReview } from './useCheckoutFlowEffects/review'
import {
  parseCheckoutStepFromSearch,
  replaceCheckoutStepInUrl,
} from './checkoutUrlState'
import { writeCheckoutGuestCartId } from '../../cart/cartPersistence'

type UseCheckoutFlowEffectsInput = {
  state: CheckoutFlowState
  dispatch: Dispatch<CheckoutFlowAction>
  token: string | null
  authenticated: boolean
  authInitializing: boolean
  cartId: string | null
  cartInitializing: boolean
  toLocalizedPath: (path: string) => string
  onCheckoutCompleted: (consumedCartId: string) => Promise<void>
}

export const useCheckoutFlowEffects = ({
  state,
  dispatch,
  token,
  authenticated,
  authInitializing,
  cartId,
  cartInitializing,
  toLocalizedPath,
  onCheckoutCompleted,
}: UseCheckoutFlowEffectsInput) => {
  const bootstrapRequestHandledRef = useRef<number | null>(null)
  const reviewLoadedForSessionRef = useRef<string | null>(null)
  const popStateSyncInFlightRef = useRef(false)

  useEffect(() => {
    if (authInitializing || cartInitializing) return

    if (!authenticated) {
      if (cartId) {
        writeCheckoutGuestCartId(cartId)
      }

      const rawNextPath =
        typeof window !== 'undefined'
          ? buildCheckoutNextPathForLoginRedirect(window.location.href)
          : toLocalizedPath('/checkout')
      const localizedCheckoutPath = toLocalizedPath('/checkout')
      const nextPath =
        rawNextPath.toLowerCase().includes('/checkout') ? rawNextPath : localizedCheckoutPath
      const loginRedirectPath = `${toLocalizedPath('/dashboard/login')}?intent=checkout&next=${encodeURIComponent(nextPath)}`
      window.location.assign(loginRedirectPath)
      return
    }

    if (bootstrapRequestHandledRef.current === state.bootstrapRequestId) return

    bootstrapRequestHandledRef.current = state.bootstrapRequestId
    dispatch({ type: 'BOOTSTRAP_STARTED' })

    let cancelled = false

    void bootstrapCheckoutFlow({
      cartId,
      token,
      dispatch,
      isCancelled: () => cancelled,
      setReviewLoadedSessionId: (sessionId) => {
        reviewLoadedForSessionRef.current = sessionId
      },
      onCheckoutCompleted,
    })

    return () => {
      cancelled = true
    }
  }, [
    authInitializing,
    authenticated,
    cartId,
    cartInitializing,
    dispatch,
    onCheckoutCompleted,
    state.bootstrapRequestId,
    toLocalizedPath,
    token,
  ])

  useEffect(() => {
    if (state.step !== 'review' || !state.session) return
    if (reviewLoadedForSessionRef.current === state.session.id && state.review) return

    let cancelled = false

    void refreshCheckoutReview({
      token,
      sessionId: state.session.id,
      dispatch,
      isCancelled: () => cancelled,
      onReviewLoaded: (sessionId) => {
        reviewLoadedForSessionRef.current = sessionId
      },
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, state.review, state.session, state.step, token])

  useEffect(() => {
    if (!state.session) return

    const onPopState = () => {
      if (popStateSyncInFlightRef.current) return
      popStateSyncInFlightRef.current = true
      try {
        const requestedStep = parseCheckoutStepFromSearch(window.location.search)
        const serverStep = resolveNextStepFromSession(state.session!)
        if (!requestedStep) {
          replaceCheckoutStepInUrl(serverStep)
          dispatch({ type: 'SET_STEP', step: serverStep })
          return
        }

        if (!canNavigateToStep(state.session!, requestedStep)) {
          replaceCheckoutStepInUrl(serverStep)
          dispatch({ type: 'SET_STEP', step: serverStep })
          return
        }

        dispatch({ type: 'SET_STEP', step: requestedStep })
      } finally {
        popStateSyncInFlightRef.current = false
      }
    }

    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [dispatch, state.session])
}
